/**
 * Scheduled Task Form Component
 *
 * Compact form for creating and editing scheduled tasks.
 * Supports multiple task types:
 * - SQL Sync Entity (Azure SQL to Dataverse sync)
 * - SQL Sync Metadata Refresh (refresh entity sync metadata)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  createScheduledTask,
  updateScheduledTask,
  deleteScheduledTask,
  getScheduledTask,
	  getScheduledTaskChildren,
	  addScheduledTaskChild,
	  reorderScheduledTaskChildren,
    getJobTypes,
	  type CreateScheduledTaskRequest,
	  type UpdateScheduledTaskRequest,
	  type ScheduleConfiguration,
	  type ScheduledTask
} from '../../lib/api';
import { fetchSharePointJobs } from '../../lib/sharepoint-api';
import { Button } from '../ui/button';
import { Form } from '../ui/form';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Save, ArrowLeft } from 'lucide-react';
import {
  buildCronExpression,
  parseCronExpression,
  type FrequencyType,
  type DayOfWeek,
  type HourlyInterval,
} from '../../lib/cronBuilder';

// Import extracted sections
import { BasicInfoSection } from './form-sections/BasicInfoSection';
import { TaskStructureSection } from './form-sections/TaskStructureSection';
import { TaskTypeSection } from './form-sections/TaskTypeSection';
import { ScheduleSection } from './form-sections/ScheduleSection';
import { AddChildDialog } from './AddChildDialog';

const scheduleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  taskStructure: z.enum(['standalone', 'parent']).default('standalone'),
  // Dynamic job type - selected from API-provided list
  jobType: z.string().optional(),
  // Dynamic parameters - populated based on job type
  parameters: z.record(z.any()).optional(),
  scheduleEnabled: z.boolean().default(true),
  scheduleType: z.enum(['interval', 'time-based']).default('interval'),
  intervalValue: z.coerce.number().min(1, 'Interval must be at least 1').optional(),
  intervalUnit: z.enum(['minutes', 'hours', 'days', 'weeks']).optional(),
  timeHour: z.coerce.number().min(0).max(23).optional(),
  timeMinute: z.coerce.number().min(0).max(59).optional(),
  frequency: z.enum(['daily', 'weekdays', 'weekends', 'specific-days']).optional(),
  specificDays: z.array(z.number().min(0).max(6)).optional(),
  hourlyInterval: z.union([z.literal(1), z.literal(2), z.literal(4), z.literal(6)]).optional(),
  timezone: z.string().min(1, 'Timezone is required'),
  maxRetries: z.coerce.number().min(0).max(10).default(3),
  retryDelayMinutes: z.coerce.number().min(1).default(15),
  onChildFailure: z.enum(['stop', 'skip', 'retry']).default('stop'),
}).refine((data) => {
  // Parent tasks don't need a job type
  if (data.taskStructure === 'parent') return true;
  // Standalone tasks need a job type
  return !!data.jobType;
}, {
  message: 'Please select a job type',
  path: ['jobType'],
}).refine((data) => {
  if (data.scheduleType === 'interval') {
    return data.intervalValue !== undefined && data.intervalUnit !== undefined;
  }
  if (data.scheduleType === 'time-based') {
    return data.timeHour !== undefined && data.timeMinute !== undefined && data.frequency !== undefined;
  }
  return true;
}, {
  message: 'Please complete all schedule fields',
  path: ['scheduleType'],
});

type ScheduleFormValues = z.infer<typeof scheduleSchema>;

interface ScheduledTaskFormProps {
  taskId?: string;
  mode: 'create' | 'edit';
  returnPath?: string;
}

export function ScheduledTaskForm({ taskId, mode, returnPath = '/scheduled-tasks' }: ScheduledTaskFormProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLegacyTask, setIsLegacyTask] = useState(false);

  // State for child tasks
  const [childTasks, setChildTasks] = useState<ScheduledTask[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [showAddChildDialog, setShowAddChildDialog] = useState(false);
  const [isAddingChild, setIsAddingChild] = useState(false);
  const [parentWithoutChildrenWarning, setParentWithoutChildrenWarning] = useState(false);

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      name: '',
      description: '',
      taskStructure: 'standalone',
      jobType: '',
      parameters: {},
      scheduleEnabled: true,
      scheduleType: 'interval',
      intervalValue: 1,
      intervalUnit: 'hours',
      timeHour: 21,
      timeMinute: 0,
      frequency: 'daily',
      specificDays: [],
      hourlyInterval: undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      maxRetries: 3,
      retryDelayMinutes: 15,
      onChildFailure: 'stop',
    },
  });

  // Watch fields
  const taskStructure = form.watch('taskStructure');
  const scheduleType = form.watch('scheduleType');
  const frequency = form.watch('frequency') as FrequencyType | undefined;

  // Fetch existing task if editing
  const { data: taskData, isLoading: isLoadingTask } = useQuery({
    queryKey: ['scheduledTask', taskId],
    queryFn: () => getScheduledTask(taskId!),
    enabled: mode === 'edit' && !!taskId,
  });

  // Load SharePoint jobs for the SharePoint task mode selector
  const { data: sharePointJobsData, isLoading: isLoadingSharePointJobs } = useQuery({
    queryKey: ['sharePointJobs'],
    queryFn: () => fetchSharePointJobs(),
  });

  const { data: jobTypesData } = useQuery({
    queryKey: ['jobTypes'],
    queryFn: getJobTypes,
    staleTime: 5 * 60 * 1000,
  });

  // Populate form with existing task data
  useEffect(() => {
    if (taskData?.task && mode === 'edit') {
      const task = taskData.task;
      const schedule = task.schedule;
      const params = (task.parameters || {}) as Record<string, unknown>;

      const legacy = !!task.entityMapping && !task.jobType;
      setIsLegacyTask(legacy);

      const hasChildren = (task.childCount ?? 0) > 0;
      const wasCreatedAsParent = task.taskGroup === 'parent-task';
      const taskStructureValue = (hasChildren || wasCreatedAsParent) ? 'parent' : 'standalone';

      let scheduleTypeValue: 'interval' | 'time-based' = 'interval';
      let timeHour = 21;
      let timeMinute = 0;
      let frequencyValue: FrequencyType = 'daily';
      let specificDays: number[] = [];
      let hourlyInterval: HourlyInterval | undefined = undefined;

      if (schedule?.cronExpression) {
        const parsed = parseCronExpression(schedule.cronExpression);
        if (parsed) {
          scheduleTypeValue = 'time-based';
          timeHour = parsed.time.hour;
          timeMinute = parsed.time.minute;
          frequencyValue = parsed.frequency;
          specificDays = parsed.specificDays || [];
          hourlyInterval = parsed.hourlyInterval;
        }
      }

      form.reset({
        name: task.name,
        description: task.description || '',
        taskStructure: taskStructureValue,
        jobType: task.jobType || '',
        parameters: params,
        scheduleEnabled: schedule?.enabled ?? true,
        scheduleType: scheduleTypeValue,
        intervalValue: schedule?.interval?.value || 1,
        intervalUnit: schedule?.interval?.unit || 'hours',
        timeHour,
        timeMinute,
        frequency: frequencyValue,
        specificDays,
        hourlyInterval,
        timezone: schedule?.timezone || 'UTC',
        maxRetries: schedule?.maxRetries ?? 3,
        retryDelayMinutes: schedule?.retryDelayMinutes ?? 15,
        onChildFailure: task.onChildFailure || 'stop',
      });

      // Load child tasks
      if ((hasChildren || wasCreatedAsParent) && taskId) {
        setIsLoadingChildren(true);
        getScheduledTaskChildren(taskId)
          .then((response) => setChildTasks(response.children || []))
          .catch((err) => {
            toast({
              title: 'Error',
              description: `Failed to load child tasks: ${err.message}`,
              variant: 'destructive',
            });
          })
          .finally(() => setIsLoadingChildren(false));
      }
    }
  }, [taskData, mode, form, taskId, toast]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: createScheduledTask,
    onSuccess: (response) => {
      console.log('[ScheduledTaskForm] Create response:', response);
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });

      // If this is a parent task, redirect to edit page so user can add children
      const isParentTask = form.getValues('taskStructure') === 'parent';
      const createdTaskId = response.task?.id;
      console.log('[ScheduledTaskForm] isParentTask:', isParentTask, 'taskId:', createdTaskId);

      if (isParentTask && createdTaskId) {
        toast({
          title: 'Task Created',
          description: 'Now add child tasks to complete the parent task setup.'
        });
        setLocation(`/scheduled-tasks/${createdTaskId}/edit`);
      } else {
        toast({ title: 'Success', description: 'Scheduled task created successfully' });
        setLocation(returnPath);
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: `Failed to create task: ${error.message}`, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduledTaskRequest }) =>
      updateScheduledTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });
      queryClient.invalidateQueries({ queryKey: ['scheduledTask', taskId] });
      toast({ title: 'Success', description: 'Scheduled task updated successfully' });
      setLocation(returnPath);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: `Failed to update task: ${error.message}`, variant: 'destructive' });
    },
  });

  // Child task handlers
  const handleAddChild = useCallback(async (
    name: string,
    childTaskMode: 'sql-sync-entity' | 'sql-sync-metadata' | 'field-sync',
    entityType?: string,
    options?: { detectChangesFirst?: boolean; includePreview?: boolean; maxRecords?: number }
  ) => {
    if (!taskId || !name.trim()) return;

    setIsAddingChild(true);
    try {
      // Build parameters based on task type
      let parameters: Record<string, unknown> | undefined;
      if (childTaskMode === 'sql-sync-entity' && entityType) {
        parameters = {
          entityType,
          detectChangesFirst: options?.detectChangesFirst ?? true,
        };
      } else if (childTaskMode === 'sql-sync-metadata') {
        parameters = {
          ...(entityType ? { entityType } : {}),
          includePreview: options?.includePreview ?? false,
        };
      } else if (childTaskMode === 'field-sync' && entityType) {
        parameters = {
          entityType,
          ...(options?.maxRecords ? { maxRecords: options.maxRecords } : {}),
        };
      }

      const childData: Partial<ScheduledTask> = {
        name: name.trim(),
        jobType: childTaskMode === 'sql-sync-entity' ? 'Sql.EntitySync' :
                 childTaskMode === 'sql-sync-metadata' ? 'Sql.RefreshMetadata' :
                 childTaskMode === 'field-sync' ? 'Sql.FieldSync' : undefined,
        parameters,
      };

      const response = await addScheduledTaskChild(taskId, childData);
      setChildTasks(prev => [...prev, response.child]);
      setShowAddChildDialog(false);

      queryClient.invalidateQueries({ queryKey: ['scheduledTask', taskId] });
      queryClient.invalidateQueries({ queryKey: ['scheduledTasks'] });

      toast({ title: 'Success', description: `Added child task: ${response.child.name}` });
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: `Failed to add child task: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setIsAddingChild(false);
    }
  }, [taskId, queryClient, toast]);

  const handleRemoveChild = useCallback(async (childId: string) => {
    if (!taskId) return;

    // Optimistically update UI
    const previousTasks = childTasks;
    const remainingTasks = childTasks.filter(c => c.id !== childId);
    setChildTasks(remainingTasks);

    try {
      // Actually delete from database
      await deleteScheduledTask(childId);

      // Renormalize execution order of remaining children (1, 2, 3...)
      if (remainingTasks.length > 0) {
        await reorderScheduledTaskChildren(taskId, remainingTasks.map(c => c.id));
      }

      // Invalidate cache to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['scheduledTask', taskId, 'children'] });
      toast({ title: 'Child Removed', description: 'The child task has been deleted.' });
    } catch (err: unknown) {
      // Revert on error
      setChildTasks(previousTasks);
      toast({
        title: 'Error',
        description: `Failed to delete child task: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    }
  }, [childTasks, taskId, queryClient, toast]);

  const handleMoveChild = useCallback(async (childId: string, direction: 'up' | 'down') => {
    if (!taskId) return;

    const currentIndex = childTasks.findIndex(c => c.id === childId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= childTasks.length) return;

    const newOrder = [...childTasks];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    setChildTasks(newOrder);

    try {
      await reorderScheduledTaskChildren(taskId, newOrder.map(c => c.id));
    } catch (err: unknown) {
      setChildTasks(childTasks);
      toast({
        title: 'Error',
        description: `Failed to reorder: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    }
  }, [taskId, childTasks, toast]);

  const handleEditChild = useCallback((childId: string) => {
    // Navigate to the child task's edit page, passing the parent task ID as return path
    const returnToParent = `/scheduled-tasks/${taskId}/edit`;
    setLocation(`/scheduled-tasks/${childId}/edit?returnPath=${encodeURIComponent(returnToParent)}`);
  }, [taskId, setLocation]);

  const onSubmit = (values: ScheduleFormValues) => {
    if (isLegacyTask) {
      toast({
        title: 'Legacy task',
        description: 'This scheduled task uses deprecated entityMapping configuration. Create a new SQL Sync task instead.',
        variant: 'destructive',
      });
      return;
    }

    // Check if editing a child task
    const editingChildTask = mode === 'edit' && !!taskData?.task?.parentTaskId;

    // Build schedule only for non-child tasks
    let schedule: ScheduleConfiguration | undefined;
    if (!editingChildTask) {
      schedule = {
        enabled: values.scheduleEnabled,
        timezone: values.timezone,
        maxRetries: values.maxRetries,
        retryDelayMinutes: values.retryDelayMinutes,
      };

      if (values.scheduleType === 'interval') {
        schedule.interval = { value: values.intervalValue!, unit: values.intervalUnit! };
      } else if (values.scheduleType === 'time-based') {
        schedule.cronExpression = buildCronExpression({
          time: { hour: values.timeHour!, minute: values.timeMinute! },
          frequency: values.frequency!,
          specificDays: values.specificDays as DayOfWeek[] | undefined,
          hourlyInterval: values.hourlyInterval,
        });
      }
    }

    const isParentTask = values.taskStructure === 'parent';

    // In edit mode, block parent tasks without children
    // In create mode, allow saving - user will be redirected to edit page to add children
    if (!editingChildTask && isParentTask && childTasks.length === 0 && mode === 'edit') {
      setParentWithoutChildrenWarning(true);
      toast({
        title: 'Add child tasks',
        description: 'Parent tasks need at least one child task.',
        variant: 'destructive',
      });
      return;
    }

    // Build request with dynamic jobType and parameters
    const request: CreateScheduledTaskRequest | UpdateScheduledTaskRequest = {
      name: values.name,
      description: values.description,
      ...(schedule && { schedule }),
      ...(values.jobType && { jobType: values.jobType }),
      ...(values.parameters && Object.keys(values.parameters).length > 0 && { parameters: values.parameters }),
      ...(!editingChildTask && { onChildFailure: values.onChildFailure }),
      ...(!editingChildTask && isParentTask && { taskGroup: 'parent-task' }),
    };

    if (mode === 'create') {
      createMutation.mutate(request as CreateScheduledTaskRequest);
    } else if (taskId) {
      updateMutation.mutate({ id: taskId, data: request as UpdateScheduledTaskRequest });
    }
  };

  // Warning state
  useEffect(() => {
    setParentWithoutChildrenWarning(taskStructure === 'parent' && childTasks.length === 0);
  }, [taskStructure, childTasks.length]);

  const isLoading = isLoadingTask;
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Check if this is a child task (has a parent)
  const isChildTask = mode === 'edit' && !!taskData?.task?.parentTaskId;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const taskName = taskData?.task?.name;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => setLocation(returnPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-xl font-bold tracking-tight">
            {mode === 'edit' && taskName ? `Edit: ${taskName}` : 'Create Scheduled Task'}
          </h2>
          {isChildTask && (
            <p className="text-sm text-muted-foreground">Child task (inherits parent schedule)</p>
          )}
          {isLegacyTask && (
            <p className="text-sm text-destructive">
              Legacy entity-mapping task (deprecated). Create a new SQL Sync task instead.
            </p>
          )}
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Row 1: Basic Info + (Schedule OR Task Type for child tasks) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BasicInfoSection form={form} />
            {isChildTask ? (
              /* Child tasks show Task Type instead of Schedule */
              <TaskTypeSection
                form={form}
                sharePointJobs={sharePointJobsData}
                sharePointJobsLoading={isLoadingSharePointJobs}
              />
            ) : (
              /* Parent/standalone tasks show Schedule */
              <ScheduleSection
                form={form}
                scheduleType={scheduleType}
                frequency={frequency}
              />
            )}
          </div>

          {/* Task Structure - only for non-child tasks */}
          {!isChildTask && (
            <TaskStructureSection
              form={form}
              taskStructure={taskStructure}
              mode={mode}
              taskId={taskId}
              childTasks={childTasks}
              isLoadingChildren={isLoadingChildren}
              parentWithoutChildrenWarning={parentWithoutChildrenWarning}
              onAddChild={() => setShowAddChildDialog(true)}
              onMoveChild={handleMoveChild}
              onRemoveChild={handleRemoveChild}
              onEditChild={handleEditChild}
            />
          )}

          {/* Task Type - only for standalone (non-parent, non-child) tasks */}
          {!isChildTask && taskStructure === 'standalone' && (
            <TaskTypeSection
              form={form}
              sharePointJobs={sharePointJobsData}
              sharePointJobsLoading={isLoadingSharePointJobs}
            />
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 pt-2">
            <Button type="submit" disabled={isPending || isLegacyTask}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              {mode === 'create' ? 'Create Task' : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setLocation(returnPath)}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>

      {/* Add Child Dialog */}
      <AddChildDialog
        open={showAddChildDialog}
        onOpenChange={setShowAddChildDialog}
        onAdd={handleAddChild}
        isAdding={isAddingChild}
        sqlSyncEntities={jobTypesData?.options?.sqlSyncEntities || []}
        fieldSyncEntities={jobTypesData?.options?.fieldSyncEntities || []}
      />
    </div>
  );
}
